import { Component, HostListener, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { ProfileUserEnums } from 'src/app/enum/profile-user-enums';
import { WindowEnums } from 'src/app/enum/window-enums';
import { OptionWindow } from 'src/app/enum/option-window-enums';
import { OptionWindowModel } from 'src/app/models/authentication/option-window.model';
import { UserModel } from 'src/app/models/authentication/user.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ConsolidatedPendingsOrdersTableModel } from 'src/app/models/tables/shopping/consolidated-pendings-orders-table.models';
import { OptionWindowService } from 'src/app/services/authentication/option-window.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { UserAuthenticatedService } from 'src/app/services/global-services/user-authenticated.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { ConsolidatedPendingsOrdersService } from 'src/app/services/shopping/consolidated-pendings-orders.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-consolidated-pendings-orders',
  templateUrl: './consolidated-pendings-orders.component.html',
  styleUrls: ['./consolidated-pendings-orders.component.scss'],
  standalone: false
})
export class ConsolidatedPendingsOrdersComponent implements OnInit {

  ascendant: boolean = true;
  columnSort: string | null = null;
  filters: ColumnResponseModel[] = [];
  generalHeader: ColumnResponseModel[] = [];
  generalData: PagedResponseModel<ConsolidatedPendingsOrdersTableModel> = new PagedResponseModel<ConsolidatedPendingsOrdersTableModel>();
  isOpened: string | undefined = undefined;
  limit: number = 10;
  nameWindow: string = 'Consolidado Pedidos Pendientes';
  page: number = 1;
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  url: string = "";
  userAuthenticated: UserModel = new UserModel();
  permissionsUser: Array<OptionWindowModel> = new Array<OptionWindowModel>();

  constructor(private consolidatedPendingsOrdersService: ConsolidatedPendingsOrdersService,
    private headerService: HeaderService,
    private title: Title,
    private userAuthenticatedService: UserAuthenticatedService,
    private router: Router,
    private toastHandlerService: ToastHandlerService,
    private windowService: WindowService,
    private optionWindowService: OptionWindowService
  ) {

  }

  async ngOnInit() {
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    this.headerService.setTitle(this.nameWindow);
    const filters = localStorage.getItem('consolidated-pendings-orders');
    if (filters) this.filters = JSON.parse(filters);
    this.getPermission();
    await Promise.all([
      this.getColumnsForGeneralConsultation(),
      this.getAllConsolidatedPendingsOrdersAsync()
    ]);
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  async ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async getColumnsForGeneralConsultation() {
    try {
      if (this.generalHeader.length === 0) {
        const response = await firstValueFrom(this.consolidatedPendingsOrdersService.getColumnsForGeneralConsultationConsolidatedAsync());
        this.generalHeader = response;
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la consulta general.', error);
    }
  }

  async getPermission() {
    let url = this.router.url as string
    url = url.substring(1);
    this.url = url;
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast(
          'error',
          `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`
        );
      }
    } else {
      localStorage.clear();
    }
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
      this.getPermissionsForUserByWindow();
    } catch (error) {
      console.error('Error al buscar los permisos por perfil.', error);
    }
  }

  async exportGeneralConsolidated() {
    if (this.userAuthenticated.profileUser.code == ProfileUserEnums.administrador || this.permissionsUser.find(x => x.code == OptionWindow.Exportar)) {
      try {
        const response = await firstValueFrom(this.consolidatedPendingsOrdersService.exportGeneralConsolidated(this.filters, this.columnSort, this.ascendant));
        const blob = response;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Consolidado pedidos internos.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error al exportar Consolidado de pedidos internos.', error);
      }
    } else {
      this.toastHandlerService.showToast(
        'warning',
        'El usuario no tiene permisos para exportar Consolidado de pedidos internos, comuniquese con el administrador del sistema.'
      );
    }
  }


  async getPermissionsForUserByWindow() {
    this.userAuthenticated = await firstValueFrom(this.userAuthenticatedService.userObservable.pipe(filter(user => user && user.userId !== 0)));
    this.permissionsUser = await firstValueFrom(this.optionWindowService.getPermissionsForUserByWindow(WindowEnums.ConsolidatedPendingsOrders));
  }

  async getAllConsolidatedPendingsOrdersAsync() {
    try {
      this.generalData = await firstValueFrom(this.consolidatedPendingsOrdersService.getAllConsolidatedPendingsOrdersAsync(
        this.page, this.limit, this.columnSort, this.ascendant, this.filters
      ));
    } catch (error) {
      console.error('Error al buscar los consolidados pendientes.', error);
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filters = this.removeDuplicates(filters);
    this.filters.forEach((filter: any) => this.processFilter(filter));
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    await this.getAllConsolidatedPendingsOrdersAsync();
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filters = this.filters.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filters.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  async onRecover() {
    this.page = 1;
    await this.getAllConsolidatedPendingsOrdersAsync();
  }
}
